import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { User, FileText, Trash2, X, Pencil } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { getProfile, upsertProfile } from '../utils/db/profiles';

const AVAILABLE_MODES = [
  'Air',
  'Road',
  'Surface',
  'Train',
  'Local'
];

const Profile = () => {
  const { user } = useAuth();
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileStatus, setProfileStatus] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedModes, setSelectedModes] = useState([]);

  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', modes: [''] });
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null); // New state for editing product
  const [newModeName, setNewModeName] = useState(''); // State for adding new global mode

  const [sectors, setSectors] = useState([]);
  const [newSector, setNewSector] = useState({ name: '', pincodes: '' });
  const [isCreatingSector, setIsCreatingSector] = useState(false);


  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm();

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      try {
        const row = await getProfile(user.id);
        if (row) {
          setValue('franchiseName', row.franchise_name || '');
          setValue('gstNo', row.gst_no || '');
          setValue('panNo', row.pan_no || '');
          setValue('address', row.address || '');
          setValue('email', row.email || '');
          setValue('mobileNo', row.mobile_no || '');
          setValue('bankName', row.bank_name || '');
          setValue('accountNo', row.account_no || '');
          setValue('ifsc', row.ifsc || '');
          setValue('branchName', row.branch_name || '');
          setValue('terms', row.terms || '');
          setValue('invoicePrefix', row.invoice_prefix || '');
          if (row.logo_url) setLogoPreview(row.logo_url);
          if (row.modes) setSelectedModes(row.modes);
          if (row.products) setProducts(row.products);
          if (row.sectors) setSectors(row.sectors);
          setProfileStatus({
            hasBasicInfo: !!(row.franchise_name && row.address && row.email && row.mobile_no),
            hasBankInfo: !!(row.bank_name && row.account_no && row.ifsc && row.branch_name),
            hasGST: !!row.gst_no,
            hasLogo: !!row.logo_url,
            hasModes: !!(row.modes && row.modes.length > 0),
            hasSectors: !!(row.sectors && row.sectors.length > 0),
            lastUpdated: row.updated_at,
          });
        }
      } catch (e) {
        // no profile yet
      }
    };
    load();
  }, [setValue, user]);

  const handleLogoChange = (e) => {
    const logoUrl = e.target.value;
    if (logoUrl) {
      setLogoPreview(logoUrl);
    }
  };

  const handleModeToggle = (mode) => {
    setSelectedModes(prev => {
      if (prev.includes(mode)) {
        return prev.filter(m => m !== mode);
      } else {
        return [...prev, mode];
      }
    });
  };

  const handleCreateProduct = () => {
    setNewProduct({ name: '', modes: [''] });
    setEditingProductId(null); // Reset editing state
    setIsCreatingProduct(true);
  };

  const handleEditProduct = (product) => {
    setNewProduct({ name: product.name, modes: product.modes.length > 0 ? product.modes : [''] });
    setEditingProductId(product.id);
    setIsCreatingProduct(true);
  };

  const handleSaveModes = async () => {
    setIsLoading(true);
    try {
      const formValues = watch(); // Get all current form values
      await upsertProfile(user.id, {
        ...formValues,
        modes: selectedModes,
        products: products,
        sectors: sectors
      });
      toast.success('Modes saved successfully');
    } catch (error) {
      console.error('Error saving modes:', error);
      toast.error('Failed to save modes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMode = () => {
    if (!newModeName.trim()) {
      toast.error('Mode name is required');
      return;
    }
    const mode = newModeName.trim();
    if (selectedModes.includes(mode)) {
      toast.error('Mode already exists');
      return;
    }
    setSelectedModes([...selectedModes, mode]);
    setNewModeName('');
  };

  const handleDeleteMode = (modeToDelete) => {
    if (window.confirm(`Are you sure you want to delete mode "${modeToDelete}"?`)) {
      setSelectedModes(selectedModes.filter(m => m !== modeToDelete));
    }
  };

  const handleAddProductMode = () => {
    if (newProduct.modes.length < 4) {
      setNewProduct(prev => ({ ...prev, modes: [...prev.modes, ''] }));
    } else {
      toast.info('Maximum 4 mode inputs allowed');
    }
  };

  const handleProductModeChange = (index, value) => {
    const newModes = [...newProduct.modes];
    newModes[index] = value;
    setNewProduct(prev => ({ ...prev, modes: newModes }));
  };

  const handleRemoveProductMode = (index) => {
    setNewProduct(prev => {
      const newModes = [...prev.modes];
      newModes.splice(index, 1);
      // Ensure there's always at least one empty mode input if all are removed
      if (newModes.length === 0) {
        return { ...prev, modes: [''] };
      }
      return { ...prev, modes: newModes };
    });
  };

  const handleSaveProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    const validModes = newProduct.modes.filter(m => m && m.trim() !== '');
    if (validModes.length === 0) {
      toast.error('At least one mode is required');
      return;
    }

    // Check for duplicate names, excluding the current product if editing
    if (products.some(p => p.name.toLowerCase() === newProduct.name.trim().toLowerCase() && p.id !== editingProductId)) {
      toast.error('Product with this name already exists');
      return;
    }

    let updatedProducts;
    let toastMessage;

    if (editingProductId) {
      // Update existing product
      updatedProducts = products.map(p =>
        p.id === editingProductId
          ? { ...p, name: newProduct.name.trim(), modes: validModes }
          : p
      );
      toastMessage = 'Product updated successfully';
    } else {
      // Add new product
      const productToAdd = {
        id: crypto.randomUUID(), // Generate a unique ID for the product
        name: newProduct.name.trim(),
        modes: validModes
      };
      updatedProducts = [...products, productToAdd];
      toastMessage = 'Product added successfully';
    }

    setIsLoading(true);
    try {
      const formValues = watch(); // Get all current form values
      await upsertProfile(user.id, {
        ...formValues,
        modes: selectedModes,
        products: updatedProducts,
        sectors: sectors
      });

      setProducts(updatedProducts);
      setNewProduct({ name: '', modes: [''] });
      setIsCreatingProduct(false);
      setEditingProductId(null); // Reset editing state
      toast.success(toastMessage);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    const updatedProducts = products.filter(p => p.id !== productId);

    setIsLoading(true);
    try {
      const formValues = watch();
      await upsertProfile(user.id, {
        ...formValues,
        modes: selectedModes,
        products: updatedProducts,
        sectors: sectors
      });

      setProducts(updatedProducts);
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSector = () => {
    setIsCreatingSector(true);
    setNewSector({ name: '', pincodes: '' });
  };

  const validatePincodes = (input) => {
    if (!input) return false;
    const parts = input.split(',').map(p => p.trim());
    const singleRegex = /^\d{6}$/;
    const rangeRegex = /^\d{6}-\d{6}$/;

    for (const part of parts) {
      if (!part) continue;
      if (!singleRegex.test(part) && !rangeRegex.test(part)) return false;
      if (rangeRegex.test(part)) {
        const [start, end] = part.split('-').map(Number);
        if (start > end) return false;
      }
    }
    return true;
  };

  const handleSaveSector = async () => {
    if (!newSector.name.trim()) {
      toast.error('Sector name is required');
      return;
    }

    // Validate pincodes
    if (!validatePincodes(newSector.pincodes)) {
      toast.error('Invalid pincode format. Use 6 digits (e.g., 110001) or ranges (e.g., 100000-200000) separated by commas.');
      return;
    }

    const updatedSectors = [...sectors, { ...newSector, id: crypto.randomUUID() }];

    try {
      setIsLoading(true);
      await upsertProfile(user.id, {
        modes: selectedModes,
        products: products,
        sectors: updatedSectors
      });
      setSectors(updatedSectors);
      setIsCreatingSector(false);
      setNewSector({ name: '', pincodes: '' });
      toast.success('Sector created');
    } catch (error) {
      toast.error('Failed to create sector');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSector = async (sectorId) => {
    if (!window.confirm('Are you sure you want to delete this sector?')) return;
    const updatedSectors = sectors.filter(s => s.id !== sectorId);
    try {
      setIsLoading(true);
      await upsertProfile(user.id, {
        modes: selectedModes,
        products: products,
        sectors: updatedSectors
      });
      setSectors(updatedSectors);
      toast.success('Sector removed');
    } catch (error) {
      toast.error('Failed to remove sector');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Use the logo URL directly from state
      const logoUrl = logoPreview;

      // Ensure logoUrl is properly passed to the database
      const saved = await upsertProfile(user.id, {
        ...data,
        logoUrl: logoUrl, // Explicitly set logoUrl to ensure it's included
        modes: selectedModes,
        products: products,
        sectors: sectors
      });

      setProfileStatus({
        hasBasicInfo: !!(saved.franchise_name && saved.address && saved.email && saved.mobile_no),
        hasBankInfo: !!(saved.bank_name && saved.account_no && saved.ifsc && saved.branch_name),
        hasGST: !!saved.gst_no,
        hasLogo: !!saved.logo_url,
        hasModes: !!(saved.modes && saved.modes.length > 0),
        hasSectors: !!(saved.sectors && saved.sectors.length > 0),
        lastUpdated: saved.updated_at,
      });

      toast.success('Profile saved to database');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your franchise profile and configuration</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`
              whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'profile'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            Profile Setup
          </button>
          <button
            onClick={() => setActiveTab('mode')}
            className={`
              whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'mode'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            Mode
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`
              whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'products'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('sectors')}
            className={`
              whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'sectors'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            Sectors
          </button>
        </nav>
      </div>

      {/* Profile Tab Content */}
      <div className={activeTab === 'profile' ? 'block' : 'hidden'}>
        {/* Profile Status */}
        {Object.keys(profileStatus).length > 0 && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Profile Status</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasBasicInfo === true ? 'bg-green-500' : (profileStatus.hasBasicInfo === false ? 'bg-red-500' : 'bg-gray-400')}`}></div>
                  <span className="text-sm font-medium">Basic Information</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasBankInfo === true ? 'bg-green-500' : (profileStatus.hasBankInfo === false ? 'bg-red-500' : 'bg-gray-400')}`}></div>
                  <span className="text-sm font-medium">Bank Details</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasGST === true ? 'bg-green-500' : (profileStatus.hasGST === false ? 'bg-red-500' : 'bg-gray-400')}`}></div>
                  <span className="text-sm font-medium">GST Number</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasLogo === true ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-medium">Company Logo</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasModes === true ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-medium">Active Modes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${profileStatus.hasSectors === true ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-medium">Sectors</span>
                </div>
              </div>
              {profileStatus.lastUpdated && (
                <p className="mt-3 text-sm text-gray-500">
                  Last updated: {new Date(profileStatus.lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Company Information
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Franchise Name *
                  </label>
                  <input
                    type="text"
                    {...register('franchiseName', {
                      required: 'Franchise name is required',
                      maxLength: {
                        value: 100,
                        message: 'Franchise name must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter franchise name"
                  />
                  {errors.franchiseName && (
                    <p className="mt-1 text-sm text-red-600">{errors.franchiseName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number *
                  </label>
                  <input
                    type="text"
                    {...register('gstNo', {
                      required: 'GST number is required',
                      pattern: {
                        value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                        message: 'Please enter a valid GST number (e.g., 27AABCM1234Z1Z5)'
                      },
                      maxLength: {
                        value: 15,
                        message: 'GST number must be 15 characters or less'
                      }
                    })}
                    maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter GST number (e.g., 27AABCM1234Z1Z5)"
                  />
                  {errors.gstNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.gstNo.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Number *
                  </label>
                  <input
                    type="text"
                    {...register('panNo', {
                      required: 'PAN number is required',
                      pattern: {
                        value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                        message: 'Please enter a valid PAN (e.g., ABCDE1234F)'
                      },
                      maxLength: {
                        value: 10,
                        message: 'PAN number must be 10 characters or less'
                      }
                    })}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter PAN (e.g., ABCDE1234F)"
                  />
                  {errors.panNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.panNo.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Start Prefix *
                  </label>
                  <input
                    type="text"
                    {...register('invoicePrefix', {
                      required: 'Invoice start prefix is required',
                      maxLength: {
                        value: 20,
                        message: 'Invoice prefix must be 20 characters or less'
                      }
                    })}
                    maxLength={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., INV/2024-"
                  />
                  {errors.invoicePrefix && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoicePrefix.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <textarea
                  {...register('address', {
                    required: 'Address is required',
                    maxLength: {
                      value: 200,
                      message: 'Address must be 200 characters or less'
                    }
                  })}
                  rows={3}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter complete address"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Email must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    {...register('mobileNo', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: 'Please enter a valid 10-digit mobile number'
                      },
                      maxLength: {
                        value: 10,
                        message: 'Mobile number must be 10 digits'
                      }
                    })}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter mobile number"
                  />
                  {errors.mobileNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.mobileNo.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Company Logo
              </h3>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Company Logo"
                      className="h-20 w-20 object-contain border border-gray-300 rounded-lg"
                    />
                  ) : (
                    <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="text"
                    value={logoPreview || ''}
                    onChange={handleLogoChange}
                    maxLength={500}
                    placeholder="Paste your logo URL here"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Paste a direct URL to your company logo image
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Tip: Logo will appear on generated invoices
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Bank Details
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    {...register('bankName', {
                      required: 'Bank name is required',
                      maxLength: {
                        value: 100,
                        message: 'Bank name must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter bank name"
                  />
                  {errors.bankName && (
                    <p className="mt-1 text-sm text-red-600">{errors.bankName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    {...register('accountNo', {
                      required: 'Account number is required',
                      maxLength: {
                        value: 20,
                        message: 'Account number must be 20 characters or less'
                      }
                    })}
                    maxLength={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter account number"
                  />
                  {errors.accountNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.accountNo.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code *
                  </label>
                  <input
                    type="text"
                    {...register('ifsc', {
                      required: 'IFSC code is required',
                      pattern: {
                        value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                        message: 'Please enter a valid IFSC code'
                      },
                      maxLength: {
                        value: 11,
                        message: 'IFSC code must be 11 characters'
                      }
                    })}
                    maxLength={11}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter IFSC code"
                  />
                  {errors.ifsc && (
                    <p className="mt-1 text-sm text-red-600">{errors.ifsc.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Branch Name *
                  </label>
                  <input
                    type="text"
                    {...register('branchName', {
                      required: 'Branch name is required',
                      maxLength: {
                        value: 100,
                        message: 'Branch name must be 100 characters or less'
                      }
                    })}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter branch name"
                  />
                  {errors.branchName && (
                    <p className="mt-1 text-sm text-red-600">{errors.branchName.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Terms & Conditions
              </h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions (will appear on invoice)
              </label>
              <textarea
                {...register('terms', {
                  required: 'Terms & Conditions are required',
                  maxLength: {
                    value: 500,
                    message: 'Terms & Conditions must be 500 characters or less'
                  }
                })}
                rows={4}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter your terms and conditions for invoices"
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {errors.terms && (
                    <p className="text-sm text-red-600">{errors.terms.message}</p>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {watch('terms')?.length || 0}/500 characters
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              * Required fields
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to reset the form?')) {
                    window.location.reload();
                  }
                }}
                className="flex items-center px-4 py-2 text-gray-700 bg-gray-200 font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset Form
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="h-5 w-5 mr-2" />
                {isLoading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Mode Tab Content (Dynamic List) */}
      {/* Mode Tab Content (Dynamic List) */}
      <div className={activeTab === 'mode' ? 'block' : 'hidden'}>
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Mode Configuration</h3>
            <p className="text-sm text-gray-500">Manage the shipping modes you offer.</p>
          </div>
          <div className="p-6">
            <div className="mb-6 flex space-x-4">
              <input
                type="text"
                value={newModeName}
                onChange={(e) => setNewModeName(e.target.value)}
                placeholder="Enter new mode (e.g., Sea, Train)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleAddMode}
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
              >
                Add Mode
              </button>
            </div>

            <div className="space-y-2">
              {selectedModes.length === 0 ? (
                <p className="text-gray-500">No modes configured.</p>
              ) : (
                selectedModes.map((mode) => (
                  <div key={mode} className="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200">
                    <span className="font-medium text-gray-900">{mode}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteMode(mode)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete Mode"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleSaveModes}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Modes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Tab Content */}
      <div className={activeTab === 'products' ? 'block' : 'hidden'}>
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Products Configuration</h3>
            <button
              onClick={handleCreateProduct}
              className="flex items-center px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
            >
              Create Product
            </button>
          </div>
          <div className="p-6">
            {/* Create Product Form */}
            {isCreatingProduct && (
              <div className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h4 className="text-md font-medium text-gray-900 mb-4">{editingProductId ? 'Edit Product' : 'New Product'}</h4>
                <div className="space-y-4">
                  <div className="flex flex-col space-y-4">
                    {/* Product Name Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter product name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    {/* Mode Selection */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Applicable Modes (Max 4)
                        </label>
                        <button
                          type="button"
                          onClick={handleAddProductMode}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          title="Add another mode"
                        >
                          + Add Mode
                        </button>
                      </div>
                      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
                        {newProduct.modes.map((mode, index) => (
                          <div key={index} className="flex items-center space-x-1 flex-shrink-0">
                            <select
                              value={mode}
                              onChange={(e) => handleProductModeChange(index, e.target.value)}
                              className="min-w-[140px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              <option value="">Select Mode</option>
                              {selectedModes.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            {newProduct.modes.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveProductMode(index)}
                                className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
                                title="Remove Mode"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select from active modes configured in the Mode tab.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => { setIsCreatingProduct(false); setEditingProductId(null); setNewProduct({ name: '', modes: [''] }); }}
                      className="px-3 py-2 text-gray-700 bg-white border border-gray-300 font-medium rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveProduct}
                      className="px-3 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
                    >
                      {editingProductId ? 'Update Product' : 'Save Product'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* List of Products */}
            {products.length === 0 && !isCreatingProduct ? (
              <p className="text-center text-gray-500">No products configured yet.</p>
            ) : (
              <div className="space-y-4">
                {products.map((product, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{product.name}</h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {product.modes.map((m, mIdx) => (
                          <span key={mIdx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="text-primary-500 hover:text-primary-700 p-1"
                        title="Edit Product"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete Product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div >

      {/* Sectors Tab Content */}
      < div className={activeTab === 'sectors' ? 'block' : 'hidden'}>
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Sectors Configuration</h3>
            <button
              onClick={handleCreateSector}
              className="flex items-center px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
            >
              + Add New
            </button>
          </div>
          <div className="p-6">
            {/* Create Sector Form */}
            {isCreatingSector && (
              <div className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h4 className="text-md font-medium text-gray-900 mb-4">New Sector</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sector Name
                    </label>
                    <input
                      type="text"
                      value={newSector.name}
                      onChange={(e) => setNewSector(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter sector name (e.g., North Zone)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pincodes
                    </label>
                    <textarea
                      value={newSector.pincodes}
                      onChange={(e) => setNewSector(prev => ({ ...prev, pincodes: e.target.value }))}
                      rows={3}
                      placeholder="e.g., 100000-200000, 300000, 400000-500000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter 6-digit pincodes or ranges separated by commas.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => setIsCreatingSector(false)}
                      className="px-3 py-2 text-gray-700 bg-white border border-gray-300 font-medium rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSector}
                      className="px-3 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
                    >
                      Save Sector
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* List of Sectors */}
            {sectors.length === 0 && !isCreatingSector ? (
              <p className="text-center text-gray-500">No sectors configured yet.</p>
            ) : (
              <div className="space-y-4">
                {sectors.map((sector, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{sector.name}</h4>
                      <p className="text-sm text-gray-600 mt-1 break-all">{sector.pincodes}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSector(sector.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete Sector"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div >
    </div >
  );
};

export default Profile;